// Once-per-hour log throttle for per-record noise (card #3995).
//
// A stuck photo row used to write a "Recovering stuck upload" line and then a
// "Photo upload aborted unexpectedly" line on every sync pass, and that loop
// on iOS produced about 12,800 entries in four days. One record is worth
// one row per hour: the first occurrence logs, repeats inside the hour are
// dropped.
//
// The window is kept in memory and also in db.session, so a page reload (which
// iOS does constantly) does not start a fresh hour. The session copy is best
// effort: if it cannot be read or written the in-memory window still applies.

import db from "./field_db"

export const LOG_THROTTLE_MS = 60 * 60 * 1000
const SESSION_KEY = "log_throttle"

const _seen = new Map()      // key -> last logged at (ms)
let _loaded = null

function _load() {
  if (!_loaded) {
    _loaded = db.session.get(SESSION_KEY)
      .then(row => {
        const stored = row?.value || {}
        for (const [key, at] of Object.entries(stored)) {
          if (typeof at === "number" && !_seen.has(key)) _seen.set(key, at)
        }
      })
      .catch(() => {})
  }
  return _loaded
}

function _prune(now) {
  for (const [key, at] of _seen) {
    if (now - at >= LOG_THROTTLE_MS) _seen.delete(key)
  }
}

// True when `key` has not been logged in the last hour, and records it as
// logged now. Never throws.
export async function shouldLogOnce(key) {
  try {
    await _load()
    const now = Date.now()
    const last = _seen.get(key)
    if (last !== undefined && now - last < LOG_THROTTLE_MS) return false

    _seen.set(key, now)
    _prune(now)
    db.session.put({ key: SESSION_KEY, value: Object.fromEntries(_seen) }).catch(() => {})
    return true
  } catch {
    return true
  }
}

// Test seam: forget every window, in memory and in the session row.
export async function _resetLogThrottle() {
  _seen.clear()
  _loaded = null
  await db.session.delete(SESSION_KEY).catch(() => {})
}
